<template>
  <b-navbar
    toggleable="lg"
    class="px-4 py-0 ap-navbar"
    :class="{ 'navbar-touch-view':touchMode }"
  >
    <div class="d-flex align-items-center">
      <b-navbar-brand qa="navbar-home">
        <img
          :src="laboratoryLogoUrl ? laboratoryLogoUrl : defaultLaboratoryLogo"
          alt="laboratory logo"
        >
      </b-navbar-brand>
      <div
        v-if="isPersonnel && !touchMode"
        class="ml-3 d-flex align-items-center"
        @click="labBoxActive=!labBoxActive"
      >
        <div
          v-if="userInformation"
          class="lab-section"
        >
          <b-button
            id="lab-pop"
            size="sm"
            class="lab-button px-3"
          >
            <i
              class="fa fa-hospital mr-2 white"
              aria-hidden="true"
            />
            <span>{{ laboratoryName }}</span>
          </b-button>
          <b-button
            v-if="visibleIncidents"
            id="incident-pop"
            size="sm"
            class="add-incident"
          >
            <i
              class="far fa-ellipsis-v white mr-1"
              aria-hidden="true"
            />
          </b-button>
        </div>
        <b-popover
          v-if="userInformation && laboratoriesConfiguration.length > 1"
          target="lab-pop"
          triggers="click blur"
          placement="bottom"
        >
          <div class="lab-pop-box">
            <div class="text-center ap-popover__header py-2">
              <span class="grey-normal">{{ $t('global.changeLab') }}</span>
            </div>
            <hr class="my-0">
            <ul style="margin-bottom:0">
              <li
                v-for="(laboratoryInformation, index) in laboratoriesConfiguration"
                :key="index"
                class="d-flex justify-content-between align-items-center"
                :class="{'active': laboratoryId === laboratoryInformation.laboratory.labId}"
                @click="changeLaboratory(laboratoryInformation)"
              >
                {{ laboratoryInformation.laboratory.labName }}
              </li>
            </ul>
          </div>
        </b-popover>
        <b-popover
          v-if="visibleIncidents"
          target="incident-pop"
          triggers="click blur"
          placement="bottom"
        >
          <div class="ap-popover">
            <ul>
              <li
                v-if="generalIncidentsSummary"
                class="d-flex align-items-center justify-content-center is-cursor-pointer"
                @click="openIncidentModal"
              >
                {{ $t('hoverBtn.seeIncidence') }}
                <vtBubble
                  :number="generalIncidentsSummary"
                  :type="true"
                  class="ml-1"
                  :tooltip="` ${$tc('global.incident', generalIncidentsSummary)}`"
                />
              </li>
              <li
                v-if="$_canCreateIncident()"
              >
                <b-button
                  @click="openIncidentFormModal"
                >
                  {{ $t('diagnosis.btns.incident') }}
                </b-button>
              </li>
            </ul>
          </div>
        </b-popover>
      </div>
      <div
        v-if="isPersonnel && touchMode"
        class="lab-section d-flex"
      >
        <b-button
          size="sm"
          class="lab-button px-3 text-left d-flex align-items-center"
        >
          <i
            class="fa fa-hospital mr-2 white"
            aria-hidden="true"
          />
          <span>{{ laboratoryName }}</span>
        </b-button>
      </div>
    </div>

    <b-navbar-toggle target="nav-collapse" />

    <div class="d-flex align-items-center ml-auto">
      <b-collapse
        id="nav-collapse"
        is-nav
      >
        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto">
          <b-nav-item
            v-if="!touchMode"
            id="navbarHome"
            v-tooltip="$t('global.home')"
            class="d-flex align-items-center navbar-icon mx-2"
            :to="{ name: 'home' }"
            exact
          >
            <div>
              <fa-icon :icon="['fas', 'home']" />
            </div>
            <span>{{ $t('global.home') }}</span>
          </b-nav-item>
          <div
            v-if="!touchMode"
            class="d-flex"
          >
            <module-nav
              v-for="module in mainModules"
              :key="module.moduleCode"
              class="mx-2"
              :module="module"
            />
          </div>
          <b-nav-item
            v-if="otherModules.length > 0 && !touchMode"
            class="d-flex align-items-center navbar-icon dropdown pt-2 ml-1 px-0"
            exact
          >
            <dropdownpoints
              class="dropdown-icon"
              :other-modules="otherModules"
            />
          </b-nav-item>
          <!-- TODO: Method pending to full screen on/off for next buttons -->
          <div
            v-if="false"
            class="d-flex"
          >
            <b-nav-item
              v-testing="'full-screen-view'"
              v-tooltip="$t('viewer.tooltips.fullScreen')"
              class="d-flex align-items-center navbar-icon dropdown pt-1 mr-2 px-0"
            >
              <i
                class="fas fa-expand"
                aria-hidden="true"
              />
            </b-nav-item>
            <b-nav-item
              v-if="false"
              v-testing="'full-screen-view'"
              v-tooltip="$t('viewer.tooltips.fullScreenOff')"
              class="d-flex align-items-center navbar-icon dropdown pt-1 mr-2 px-0"
            >
              <i
                class="far fa-compress-arrows-alt"
                aria-hidden="true"
              />
            </b-nav-item>
          </div>
          <b-nav-item
            v-if="touchMode"
            v-testing="'desktop-view'"
            v-tooltip="$t('laboratory.grid.desktopMode')"
            class="d-flex align-items-center navbar-icon dropdown pt-1 ml-1 px-0"
            @click="goToDesktopLaboratory"
          >
            <i
              class="fas fa-laptop"
              aria-hidden="true"
            />
          </b-nav-item>
          <b-nav-item class="d-flex align-items-center navbar-icon mx-2 d-flex align-items-center">
            <div class="btn-line mx-1" />
          </b-nav-item>
          <!--Laboratory only-->
          <b-nav-item
            v-if="$route.name === routeNameOfLaboratoryModule"
            v-testing="'touch-mode'"
            v-tooltip="$t('laboratory.grid.touchMode')"
            class="touch-icon d-flex align-items-center navbar-icon mx-0"
            exact
            @click="goToLaboratoryTouchView"
          >
            <div>
              <i
                class="far fa-hand-point-up"
                aria-hidden="true"
              />
            </div>
          </b-nav-item>
          <b-nav-item
            v-if="$route.name === routeNameOfLaboratoryModule"
            class="d-flex align-items-center navbar-icon mx-2 d-flex align-items-center"
          >
            <div class="btn-line mx-1" />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
      <b-navbar-nav>
        <!--User, notifications and search section-->
        <notifications-section
          ref="notificationsSection"
          class="navbar-notification-section mr-5 mr-lg-0"
          :touch-mode="touchMode"
          @notificationSelected="showNotification($event)"
        />
      </b-navbar-nav>
      <b-collapse
        id="nav-collapse"
        is-nav
      >
        <b-navbar-nav class="ml-auto">
          <b-nav-item class="d-flex align-items-center navbar-icon mx-2 d-flex align-items-center">
            <div class="btn-line mx-1" />
          </b-nav-item>
          <div
            class="d-flex align-items-center"
            style="position:relative;width: 170px"
          >
            <user-nav
              v-if="!showSearchBar"
              :touch-mode="touchMode"
              @log-out="logOut()"
            />
            <!--Search layout-->
            <div
              v-if="showSearchBar && isPersonnel"
              class="search-block d-flex align-items-center ml-3"
            >
              <b-form-input
                v-model="locatorInput"
                v-focus
                size="sm"
                class="search-tracker global-locator-input mr-sm-2"
                qa="nav-bar-global-locator-input"
                :placeholder="$t('global.searchGlobal')"
                @keyup.enter="searchGlobally"
              />
            </div>
          </div>
          <div
            v-if="isPersonnel"
            class="search-box ml-3 d-flex align-items-center"
          >
            <b-button
              v-if="!showSearchBar"
              v-tooltip="$t('global.searchGlobal')"
              v-shortkey="['ctrl', 'shift', 'f']"
              class="end-btn"
              qa="nav-bar-global-locator-button"
              @shortkey.prevent="showGlobalLocator"
              @click="showGlobalLocator"
            >
              <i
                class="fal fa-map-marker-alt"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-if="showSearchBar"
              class="end-btn"
              qa="nav-bar-close-global-locator-button"
              @click="closeAndClearGlobalLocator"
            >
              <i
                class="far fa-times"
                aria-hidden="true"
              />
            </b-button>
          </div>
          <div class="ml-4 d-flex align-items-center">
            <b-button
              id="navbar-help-pop"
              v-tooltip="$t('global.help')"
              class="end-btn mr-4"
              qa="userNav-help-button"
            >
              <i
                class="fal fa-question-circle"
                style="width:15px"
                aria-hidden="true"
              />
            </b-button>
            <b-popover
              target="navbar-help-pop"
              placement="bottomleft"
              triggers="click blur"
            >
              <div class="pop-help ap-popover p-0">
                <b-button
                  class="dropdown-item p-2"
                  @click="$refs.aboutModalWrapper.show()"
                >
                  <b-row class="more-menu-item px-4">
                    <b-col class="text-left">
                      {{ $t('component.helpPopover.about') }}
                    </b-col>
                  </b-row>
                </b-button>
                <b-button class="dropdown-item p-2">
                  <b-row class="more-menu-item px-4">
                    <b-col class="text-left">
                      {{ $t('component.helpPopover.userManual') }}
                    </b-col>
                  </b-row>
                </b-button>
                <b-button
                  class="dropdown-item p-2"
                  @click="$refs.shortcutsAndVoiceCommandsWrapper.show()"
                >
                  <b-row class="more-menu-item px-4">
                    <b-col class="text-left">
                      {{ $t('component.helpPopover.shortcutsAndVoiceCommands') }}
                    </b-col>
                  </b-row>
                </b-button>
                <b-button class="dropdown-item p-2">
                  <b-row class="more-menu-item px-4">
                    <b-col class="text-left">
                      {{ $t('component.helpPopover.technicalSupport') }}
                    </b-col>
                  </b-row>
                </b-button>
                <b-button class="dropdown-item p-2">
                  <b-row class="more-menu-item px-4">
                    <b-col class="text-left">
                      {{ $t('component.helpPopover.contact') }}
                    </b-col>
                  </b-row>
                </b-button>
              </div>
            </b-popover>
            <b-button
              v-shortkey="['ctrl', 'f1']"
              v-tooltip="$t('global.signOut')"
              class="end-btn ml-1"
              qa="userNav-logout-button"
              @click="openLogoutModal"
              @shortkey.prevent="openLogoutModal"
            >
              <i
                class="far fa-sign-out"
                style="width:15px"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </b-navbar-nav>
      </b-collapse>
    </div>

    <!--Responsive-->
    <div class="d-flex icon-res">
      <div class="responsive-nav d-flex align-items-center d-none d-md-block d-lg-none">
        <b-button
          class="end-btn"
          @click="isResponsive=!isResponsive"
        >
          <i
            v-if="!isResponsive"
            class="far fa-bars"
            style="width:15px"
            aria-hidden="true"
          />
          <i
            v-if="isResponsive"
            class="far fa-times"
            style="width:15px"
            aria-hidden="true"
          />
        </b-button>
      </div>
    </div>
    <div
      v-if="isResponsive"
      class="responsive-panel"
    >
      <div class="user-section-responsive">
        <user-nav
          class="ml-3"
          :responsive="true"
          :touch-mode="touchMode"
          @log-out="logOut()"
        />
      </div>
      <div class="global-search-section-responsive">
        <b-container>
          <b-row class="search-box w-100">
            <b-col class="d-flex align-items-center">
              <b-form-input
                v-model="locatorInput"
                v-focus
                size="sm"
                class="search-tracker mr-sm-2"
                :placeholder="$t('global.searchGlobal')"
                @keyup.enter="searchGlobally"
              />
            </b-col>
            <b-col
              cols="auto"
              class="p-0 d-flex align-items-center justify-content-center"
            >
              <b-button
                class="btn__icon btn__icon--primary  pl-2"
                @click="searchGlobally"
              >
                <i
                  class="fal fa-map-marker-alt"
                  aria-hidden="true"
                />
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
      <div
        v-if="!touchMode"
        class="d-flex align-items-center"
      >
        <ul class="responsive-option">
          <router-link :to="{ name: 'home' }">
            <li>
              <div class="d-flex align-items-center">
                <i
                  class="far fa-home"
                  aria-hidden="true"
                />
                <span>{{ $t('global.home') }}</span>
              </div>
            </li>
          </router-link>
        </ul>
      </div>
      <div v-if="!touchMode">
        <module-nav
          v-for="module in modules"
          :key="module.moduleCode"
          :responsive="true"
          class="responsive-module-nav"
          :module="module"
        />
      </div>
      <div
        v-if="touchMode"
        class="d-flex align-items-center"
      >
        <ul class="responsive-option">
          <li @click="goToDesktopLaboratory">
            <div class="d-flex align-items-center">
              <i
                class="far fa-laptop"
                aria-hidden="true"
              />
              <span>{{ $t('laboratory.grid.desktopMode') }}</span>
            </div>
          </li>
        </ul>
      </div>
      <div
        v-if="$route.name === routeNameOfLaboratoryModule && !touchMode"
        class="d-flex align-items-center"
      >
        <ul class="responsive-option">
          <li @click="goToLaboratoryTouchView">
            <div class="d-flex align-items-center">
              <i
                class="far fa-hand-point-up"
                aria-hidden="true"
              />
              <span>{{ $t('laboratory.grid.touchMode') }}</span>
            </div>
          </li>
        </ul>
      </div>
      <div>
        <hr class="my-2">
      </div>
      <div class="d-flex align-items-center">
        <ul class="responsive-option">
          <li
            class="d-flex align-items-center justify-content-between"
            :class="!helpVisible ? null : 'open'"
            :aria-expanded="helpVisible ? 'true' : 'false'"
            aria-controls="collapse-user-options"
            @click="helpVisible = !helpVisible"
          >
            <div class="d-flex align-items-center">
              <i
                class="far fa-question-circle"
                aria-hidden="true"
              />
              <span>{{ $t('global.help') }}</span>
            </div>
            <div>
              <i
                v-if="!helpVisible"
                class="far fa-chevron-down"
                aria-hidden="true"
              />
              <i
                v-if="helpVisible"
                class="far fa-chevron-up"
                aria-hidden="true"
              />
            </div>
          </li>
        </ul>
      </div>
      <b-collapse
        id="collapse-user-options"
        v-model="helpVisible"
      >
        <ul class="responsive-option child">
          <li @click="$refs.aboutModalWrapper.show()">
            <div class="d-flex align-items-center">
              <span>{{ $t('component.helpPopover.about') }}</span>
            </div>
          </li>
          <li>
            <div class="d-flex align-items-center">
              <span>{{ $t('component.helpPopover.userManual') }}</span>
            </div>
          </li>
          <li @click="$refs.shortcutsAndVoiceCommandsWrapper.show()">
            <div class="d-flex align-items-center">
              <span>{{ $t('component.helpPopover.shortcutsAndVoiceCommands') }}</span>
            </div>
          </li>
          <li>
            <div class="d-flex align-items-center">
              <span>{{ $t('component.helpPopover.technicalSupport') }}</span>
            </div>
          </li>
          <li>
            <div class="d-flex align-items-center">
              <span>{{ $t('component.helpPopover.contact') }}</span>
            </div>
          </li>
        </ul>
      </b-collapse>
      <div class="d-flex align-items-center">
        <ul class="responsive-option">
          <li
            qa="userNav-logout-button"
            @click="openLogoutModal"
          >
            <div class="d-flex align-items-center">
              <i
                class="far fa-sign-out"
                aria-hidden="true"
              />
              <span>{{ $t('global.signOut') }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree
        :is-general="true"
        @incident-added="onIncidentAdded($event)"
        @incident-closed="onIncidentClosed($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="incidentFormModal">
      <incident-form-modal
        id="incidentFormModal"
        :core-entity="'GENERAL'"
        @incident-added="onIncidentAdded($event)"
        @incident-closed="onIncidentClosed($event)"
      />
    </modal-wrapper>
    <modal-logout
      v-if="!touchMode"
      ref="logoutModal"
    />
    <modal-logout-tracking
      v-if="touchMode"
      ref="logoutModal"
    />
    <modal-wrapper ref="notificationDetailModalWrapper">
      <notification-detail-modal
        ref="notificationDetailModal"
        @reload="handleReadNotification($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="shortcutsAndVoiceCommandsWrapper">
      <shortcuts-and-voice-commands-modal ref="shortcutsAndVoiceCommands" />
    </modal-wrapper>
    <modal-wrapper ref="aboutModalWrapper">
      <about-modal ref="aboutModal" />
    </modal-wrapper>
  </b-navbar>
</template>

<script>
import moment from 'moment'
import UserNav from './UserNav'
import ModuleNav from './ModuleNav'
import sessionStore from '@/session/sessionStore'
import { store } from '@/store/store'
import { StoreUtils } from '@/shared/util/store-utils'
import Dropdownpoints from '@/components/NavBar/Dropdownpoints'
import VtBubble from '@/components/common/VtBubble'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import toastr from 'toastr'
import commonRequestService from '@/api/resources/common/commonRequestService'
import caseServices from '@/api/resources/common/commonCaseServices'
import commonSampleService from '@/api/resources/common/commonSampleService'
import portionServices from '@/api/resources/common/commonPortionServices'
import subportionServices from '@/api/resources/common/commonSubportionServices'
import ModalLogout from '@/components/modals/ModalLogout'
import ModalLogoutTracking from '@/modules/laboratoryModule/laboratoryTracking/modals/ModalLogoutTracking'
import incidentServices from '@/api/resources/incident/incidentServices'
import CustomConfigResource from '@/api/resources/ui/CustomConfigResource'
import laboratoryAttachmentService from '@/api/resources/attachment/laboratoryAttachmentService'
import defaultLogo from '@/img/anapath-logo-white.png'
import NotificationsSection from '@/components/NavBar/notifications/NotificationsSection'
import NotificationDetailModal from '@/components/NavBar/notifications/NotificationDetailModal'
import { uniqBy } from 'lodash'
import ShortcutsAndVoiceCommandsModal from '@/components/help/shortcutsAndVoiceCommands/ShortcutsAndVoiceCommandsModal'
import AboutModal from '@/components/help/AboutModal'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import moduleResource from '@/api/resources/ModuleResource'
import { router } from '@/router'
import { auth } from '@/api/auth'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto.ts'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'NavBar',
    components: {
        NotificationsSection,
        UserNav,
        ModuleNav,
        Dropdownpoints,
        VtBubble,
        CommonIncidentModalTree,
        IncidentFormModal,
        ModalLogout,
        ModalLogoutTracking,
        NotificationDetailModal,
        ShortcutsAndVoiceCommandsModal,
        AboutModal
    },
    mixins: [
        IncidentOperationGuardMixin
    ],
    props: {
        touchMode: { type: Boolean, default: false }
    },
    data() {
        return {
            locatorInput: null,
            entity: null,
            showSearchBar: false,
            menuActive: false,
            isResponsive: false,
            labBoxActive: false,
            sessionState: sessionStore.state,
            greeting: '',
            generalIncidentsSummary: 0,
            checkInterval: null,
            uiComponentLaboratoryViewMode: 'uicomponent.laboratory.view.mode',
            laboratoryLogoUrl: null,
            defaultLaboratoryLogo: defaultLogo,
            helpVisible: false
        }
    },
    computed: {
        laboratoryName() {
            return StoreUtils.selectedLaboratoryConfiguration?.laboratory.labName
        },
        laboratoryId() {
            return store.state.labId
        },
        routeNameOfLaboratoryTouchView() {
            return { name: 'ContentTracking' }
        },
        routeNameOfLaboratoryModule() {
            return 'laboratoryModule'
        },
        userInformation() {
            return store.state.userInformation
        },
        isPersonnel() {
            return this.userInformation?.userType === UserType.PERSONNEL
        },
        mainModules() {
            // to be displayed in the navbar
            if (this.modules.length) {
                return this.modules.slice(0, 3)
            }
            return this.modules
        },
        otherModules() {
            // to be displayed in the module menu
            if (this.modules.length < 3) {
                return []
            }
            return this.modules.slice(3, this.modules.length)
        },
        modules() {
            return store.state.modules
        },
        fullName() {
            if (this.userInformation) {
                if (this.userInformation.petitioner) {
                    return [this.userInformation.petitioner.name,
                        this.userInformation.petitioner.middleName,
                        this.userInformation.petitioner.surname1,
                        this.userInformation.petitioner.surname2].join(' ')
                }
                if (this.userInformation.personnel) {
                    return [this.userInformation.personnel.name,
                        this.userInformation.personnel.middleName,
                        this.userInformation.personnel.surname1,
                        this.userInformation.personnel.surname2].join(' ')
                }
            }
            return ''
        },
        userLoggedIn() {
            return store.state.userLoggedIn
        },
        laboratoriesConfiguration() {
            return store.state.laboratoriesConfiguration.filter(configuration => configuration.modules.length)
        },
        regex() {
            return store.state.regex
        },
        visibleIncidents() {
            return this.userInformation
        }
    },
    async created() {
        if (store.state.showGreetMessageFlag) {
            this.showAlertGreet()
            store.commit('updateShowGreetMessageFlag', false)
        }

        if (this.isPersonnel) {
            await this._loadGeneralIncidentsSummary(this.laboratoryId)
            this._initializeCheckInterval(this.laboratoryId)
            await this.loadLogo(this.laboratoryId)
        }
    },
    mounted() {
        this.$eventHub.$on('show-in-global-locator', this.showInGlobalLocator)
        this.$root.$on('change:laboratory:configuration:logo', this.changeLogo)
    },
    beforeDestroy() {
        this._clearInterval()
        this.$eventHub.$off('show-in-global-locator', this.showInGlobalLocator)
        this.$root.$off('change:laboratory:configuration:logo', this.changeLogo)
    },
    methods: {
        async showInGlobalLocator(label) {
            this.locatorInput = label
            await this.searchGlobally()
            this.showSearchBar = false
        },
        changeLogo(logo) {
            this.laboratoryLogoUrl = logo ? window.URL.createObjectURL(logo) : null
        },
        _initializeCheckInterval(laboratoryId) {
            this.checkInterval =
                HelperUtils.setSummaryInterval(
                    () => this._loadGeneralIncidentsSummary(laboratoryId))
        },
        _clearInterval() {
            if (this.checkInterval !== null) {
                clearInterval(this.checkInterval)
            }
        },
        async goToLaboratoryTouchView() {
            await CustomConfigResource.save(this.uiComponentLaboratoryViewMode, { touchView: true })
            await router.push(this.routeNameOfLaboratoryTouchView)
        },
        async goToDesktopLaboratory() {
            await CustomConfigResource.save(this.uiComponentLaboratoryViewMode, { touchView: false })
            await router.push({ name: 'laboratoryModule' })
        },
        async _loadGeneralIncidentsSummary(laboratoryId) {
            if (this.modules.length) {
                const response =
                    await PromiseUtils.toCallSecure(
                        incidentServices.getGeneralIncidentsSummary(laboratoryId))
                if (response !== null && !isNaN(response)) {
                    this.generalIncidentsSummary = response
                }
            }
        },
        async onIncidentAdded(event) {
            await this._loadGeneralIncidentsSummary(this.laboratoryId)
            this.$emit('incident-added', event)
        },
        async onIncidentClosed(event) {
            await this._loadGeneralIncidentsSummary(this.laboratoryId)
            this.$emit('incident-closed', event)
        },
        async searchEntities(label, regexes) {
            let foundEntities = []
            const laboratoryId = this.laboratoryId
            await Promise.all(regexes.map(async (regex) => {
                try {
                    switch (regex.identifierType.idtScope) {
                    case IdentifierTypeScope.REQUEST:
                        const request = await commonRequestService.findByLabel(laboratoryId, label)
                        request.forEach(element => {
                            foundEntities.push({
                                entityType: IdentifierTypeScope.REQUEST,
                                entityValue: element,
                                laboratoryId
                            })
                        })
                        break
                    case IdentifierTypeScope.CASE:
                        const caseStudy = await caseServices.findByLabel(laboratoryId, label)
                        caseStudy.forEach(element => {
                            foundEntities.push({
                                entityType: IdentifierTypeScope.CASE,
                                entityValue: element,
                                laboratoryId
                            })
                        })
                        break
                    case IdentifierTypeScope.SAMPLE:
                        const sample = await commonSampleService.findByLabel(laboratoryId, label)
                        sample.forEach(element => {
                            foundEntities.push({
                                entityType: IdentifierTypeScope.SAMPLE,
                                entityValue: element,
                                laboratoryId
                            })
                        })
                        break
                    case IdentifierTypeScope.PORTION:
                        const portion = await portionServices.findByLabel(laboratoryId, label)
                        portion.forEach(element => {
                            foundEntities.push({
                                entityType: IdentifierTypeScope.PORTION,
                                entityValue: element,
                                laboratoryId
                            })
                        })
                        break
                    case IdentifierTypeScope.SUBPORTION:
                        const subportion = await subportionServices.findByLabel(laboratoryId, label)
                        subportion.forEach(element => {
                            foundEntities.push({
                                entityType: IdentifierTypeScope.SUBPORTION,
                                entityValue: element,
                                laboratoryId
                            })
                        })
                        break
                    }
                } catch (e) {
                    if (e.response.status !== 404) {
                        throw e
                    }
                }
            }))
            return foundEntities
        },
        async searchGlobally() {
            this.locatorInput = this.locatorInput?.trim()
            const searchGloballyRegexes = this.regex.filter(regex =>
                regex.identifierType.idtScope === IdentifierTypeScope.CASE ||
                regex.identifierType.idtScope === IdentifierTypeScope.SAMPLE ||
                regex.identifierType.idtScope === IdentifierTypeScope.REQUEST ||
                regex.identifierType.idtScope === IdentifierTypeScope.PORTION ||
                regex.identifierType.idtScope === IdentifierTypeScope.SUBPORTION
            )
            const matchingRegexes =
                uniqBy(
                    searchGloballyRegexes.filter(element => (new RegExp(`^(?:${element.regex})$`)).test(this.locatorInput)),
                    'identifierType.code')
            if (matchingRegexes.length === 0) {
                this.showErrorModalGlobal(this.$t('component.modalWarning.inputValidEntityType'))
                this.locatorInput = ''
                return
            }

            const foundEntities =
                await PromiseUtils.toCallUnique(
                    this.searchEntities(this.locatorInput, matchingRegexes))
            if (foundEntities !== null) {
                if (foundEntities.length === 0) {
                    this.showErrorModalGlobal(this.$t('globalLocator.error.noElementFound'))
                    this.locatorInput = ''
                    return
                }
                this.$emit('show-global-locator', [
                    this.$tc('globalLocator.labels.title'),
                    this.locatorInput,
                    foundEntities
                ])
                this.locatorInput = ''
                this.showSearchBar = !this.showSearchBar
            }
        },
        showGlobalLocator() {
            this.showSearchBar = true
        },
        closeAndClearGlobalLocator() {
            this.locatorInput = ''
            this.showSearchBar = false
        },
        openIncidentModal() {
            this.$refs.incidentModalTreeWrapper.show()
        },
        openIncidentFormModal() {
            this.$refs.incidentFormModal.show(null, {})
        },
        openLogoutModal() {
            this.$refs.logoutModal.show()
        },
        async changeLaboratory(lab) {
            if (lab.laboratory.labId !== this.laboratoryId) {
                const userDefaultLabModule =
                    store.state.defaultAccess?.defaultLabModules.find(
                        (labModule) => labModule.laboratory.labId === lab.laboratory.labId)
                const routeName = moduleResource.getModuleRouteNameFromModuleCode(userDefaultLabModule?.moduleCode)
                if (routeName) {
                    if (router.currentRoute.meta.checkChanges) {
                        // $emit event with new route to redirect
                        this.$eventHub.$emit('changeRouteWithCheckChanges', {
                            route: { name: routeName },
                            postFnToRun: this.postChangeLaboratory.bind(this, lab)
                        })
                    } else {
                        await router.push({ name: routeName })
                        await this.postChangeLaboratory(lab)
                    }
                    this.$root.$emit('bv::hide::popover')
                }
            }
        },
        async postChangeLaboratory(lab) {
            await this.loadLogo(lab.laboratory.labId)
            sessionStore.updateLabId(lab.laboratory.labId)
            this._clearInterval()
            if (this.isPersonnel) {
                await this._loadGeneralIncidentsSummary(lab.laboratory.labId)
                this._initializeCheckInterval(lab.laboratory.labId)
            }
        },
        async loadLogo(laboratoryId) {
            const response =
                await PromiseUtils.toCallSecure(
                    laboratoryAttachmentService.getFile(laboratoryId),
                    error => PromiseUtils.handleAxiosError(error, [404]))
            this.laboratoryLogoUrl = response
                ? window.URL.createObjectURL(response)
                : null
        },
        logOut() {
            auth.logOut()
        },
        showAlertGreet() {
            this.greeting = `${this.getGreetingTime(moment())} ${this.fullName}
            <p><small>${this.$t('global.lastAccess')}: ${moment(this.userInformation.penultimateAccess).format('llll')}</small></p>`
            toastr.info(this.greeting, '', {
                closeButton: true,
                timeOut: 4000,
                positionClass: 'toast-top-right',
                hideDuration: 500
            })
        },
        getGreetingTime(m) {
            let g = null

            if (!m || !m.isValid()) {
                return
            }

            let splitAfternoon = 12
            let splitEvening = 20
            let currentHour = parseFloat(m.format('HH'))

            if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
                g = this.$t('global.greetingEvening')
            } else if (currentHour >= splitEvening) {
                g = this.$t('global.greetingNight')
            } else {
                g = this.$t('global.greetingMorning')
            }

            return g
        },
        showNotification(notification) {
            this.$refs.notificationDetailModalWrapper.show([notification])
        },
        handleReadNotification(notification) {
            this.$refs.notificationsSection.handleReadNotification(notification)
        }
    }
}
</script>
